import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  ModalBody,
  ModalFooter,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { FormEvent, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import { ACCESS_CONTROL_KEY } from '../../../../hooks/useAccessControl';
import {
  AccessControlItem,
  AccessControlKind,
  AccessControlUser,
  accessControlError,
  accessControlService,
} from '../../../../services/accessControlService';
import {
  KIND_LABEL,
  identificationError,
  plural,
  toIdentification,
} from '../../../../utils/accessControl';
import { apllyToast } from '../../../Global/Toast2.0';
import {
  CopySource,
  ItemUsersEditor,
} from '../../AccessControl/ItemUsersEditor';
import { ModalComponent } from '..';

interface Props {
  kind: AccessControlKind;
  isOpen: boolean;
  onClose: () => void;
  // Sem item: criação.
  item?: AccessControlItem;
  items: AccessControlItem[];
  users: AccessControlUser[];
  usersLoading: boolean;
  copySources: CopySource[];
}

// Códigos que o sistema usa diretamente: não mudam.
const LOCKED = ['user_adm', 'master_user'];

export function ModalAccessItem({
  kind,
  isOpen,
  onClose,
  item,
  items,
  users,
  usersLoading,
  copySources,
}: Props) {
  const queryClient = useQueryClient();
  const label = KIND_LABEL[kind];
  const [name, setName] = useState('');
  const [identification, setIdentification] = useState('');
  // Enquanto não editar à mão, a identificação acompanha o nome.
  const [autoIdentification, setAutoIdentification] = useState(true);
  const [description, setDescription] = useState('');
  const [accessesIds, setAccessesIds] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setName(item?.name || '');
    setIdentification(item?.identification || '');
    setAutoIdentification(!item);
    setDescription(item?.description || '');
    setAccessesIds([]);
    setSubmitted(false);
  }, [isOpen, item]);

  const identificationChanged =
    !!item && identification.trim() !== item.identification;
  const locked = !!item && LOCKED.includes(item.identification.trim());
  // Código antigo fora do padrão continua aceito se não for alterado.
  const idError =
    item && !identificationChanged
      ? undefined
      : identificationError(identification, items, item?.id);
  const nameError = !name.trim() ? 'Informe o nome.' : undefined;

  const handleNameChange = (value: string) => {
    setName(value);

    if (autoIdentification) {
      setIdentification(toIdentification(value));
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);

    if (nameError || idError) {
      return;
    }

    setSaving(true);
    try {
      if (item) {
        await accessControlService.updateItem(kind, item.id, {
          name: name.trim(),
          description: description.trim(),
          ...(identificationChanged
            ? { identification: identification.trim() }
            : {}),
        });
        apllyToast('success', `${label.title} atualizada.`);
      } else {
        await accessControlService.createItem(kind, {
          name: name.trim(),
          identification: identification.trim(),
          description: description.trim(),
          accesses_ids: accessesIds,
        });
        apllyToast(
          'success',
          accessesIds.length
            ? `${label.title} criada e atribuída a ${plural(
                accessesIds.length,
                'usuário',
                'usuários',
              )}.`
            : `${label.title} criada.`,
        );
      }

      await queryClient.invalidateQueries(ACCESS_CONTROL_KEY);
      onClose();
    } catch (err) {
      apllyToast(
        'error',
        accessControlError(err, `Não foi possível salvar a ${label.one}.`),
      );
    } finally {
      setSaving(false);
    }
  };

  const title = item ? `Editar ${label.one}` : `Adicionar nova ${label.one}`;

  return (
    <ModalComponent
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      sizeSpecifyc="2xl"
      scrollBehavior="inside"
      closeOnOverlayClick={false}
    >
      <Box
        as="form"
        onSubmit={handleSubmit}
        display="flex"
        flexDirection="column"
        flex="1"
        minH="0"
        noValidate
      >
        <ModalBody>
          <Stack spacing="5">
            {kind === 'role' && (
              <Text fontSize="sm" color="gray.400">
                Funções agrupam pessoas pelo papel (ex.: Gestor de processos). O
                que libera telas e relatórios no SAMASC são as permissões.
              </Text>
            )}

            <FormControl isInvalid={submitted && !!nameError} isRequired>
              <FormLabel htmlFor="access-item-name">Nome</FormLabel>
              <Input
                id="access-item-name"
                value={name}
                onChange={event => handleNameChange(event.target.value)}
                maxLength={150}
                placeholder={
                  kind === 'permission'
                    ? 'Ex.: BI Vendas por filial'
                    : 'Ex.: Gestor de compras'
                }
                autoFocus
              />
              <FormErrorMessage>{nameError}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!identification && !!idError} isRequired>
              <FormLabel htmlFor="access-item-identification">
                Identificação (código usado pelo sistema)
              </FormLabel>
              <Input
                id="access-item-identification"
                value={identification}
                onChange={event => {
                  setAutoIdentification(false);
                  setIdentification(event.target.value);
                }}
                fontFamily="mono"
                maxLength={80}
                isReadOnly={locked}
                placeholder="ex.: bi_vendas_por_filial"
              />
              {identification && idError ? (
                <FormErrorMessage>{idError}</FormErrorMessage>
              ) : (
                <FormHelperText>
                  {locked
                    ? 'Código usado diretamente pelo sistema: não pode ser alterado.'
                    : 'Gerada a partir do nome. Só letras minúsculas sem acento, números e "_".'}
                </FormHelperText>
              )}
              {submitted && !identification && (
                <Text fontSize="sm" color="red.400" mt="1">
                  Informe a identificação.
                </Text>
              )}
            </FormControl>

            {identificationChanged && item && item.total_accesses > 0 && (
              <Alert status="warning" color="orange.900" borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  Esta {label.one} está com{' '}
                  {plural(item.total_accesses, 'usuário', 'usuários')}. Se o
                  sistema verifica o código antigo em alguma tela, quem tem a{' '}
                  {label.one} perde esse acesso até o código ser ajustado.
                </AlertDescription>
              </Alert>
            )}

            <FormControl>
              <FormLabel htmlFor="access-item-description">Descrição</FormLabel>
              <Textarea
                id="access-item-description"
                value={description}
                onChange={event => setDescription(event.target.value)}
                maxLength={500}
                rows={2}
                placeholder="O que esta permissão libera e para quem"
              />
            </FormControl>

            {!item && (
              <>
                <Divider borderColor="gray.600" />
                <Box>
                  <Text fontWeight="bold">Atribuir a usuários</Text>
                  <Text fontSize="sm" color="gray.500" mb="3">
                    Opcional. Os usuários já saem com a {label.one} ao salvar
                    (precisam atualizar a página do SAMASC para ver o acesso).
                  </Text>
                  <ItemUsersEditor
                    users={users}
                    isLoading={usersLoading}
                    initialIds={[]}
                    value={accessesIds}
                    onChange={setAccessesIds}
                    copySources={copySources}
                  />
                </Box>
              </>
            )}
          </Stack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor="gray.600">
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button colorScheme="blue" type="submit" isLoading={saving}>
            {item ? 'Salvar' : 'Criar'}
          </Button>
        </ModalFooter>
      </Box>
    </ModalComponent>
  );
}
